import type {
  AssistantStreamChunk,
  CancelCause,
  ContextUsage,
  ModelAttachment,
  ModelMessage,
  ModelUsage,
  SessionLog,
} from '@tnega/session'
import type { ToolDefinition, ToolResult } from '@tnega/tools'

export type AgentCancelCause = CancelCause

export type AgentFinishReason =
  | 'stop'
  | 'tool_calls'
  | 'length'
  | 'max_turns'
  | 'max_steps'
  | 'error'
  | 'cancelled'

export interface LLMToolCall {
  id: string
  name: string
  arguments: unknown
}

export interface LLMCompletion {
  content?: string
  /** Reasoning text the provider exposed alongside the answer, when it exposed any. */
  reasoning?: string
  toolCalls?: LLMToolCall[]
  finishReason: AgentFinishReason
  /** Cost the provider reported for this response, when it reported any. */
  usage?: ModelUsage
}

export interface CompleteOptions {
  maxSteps?: number
  signal?: AbortSignal
  provider?: string
  model?: string
  temperature?: number
  /**
   * Output cap for this call, overriding the adapter's configured default.
   * Callers that need a different budget from the conversation's — a
   * structured side request, for example — set it per call.
   */
  maxTokens?: number
  /**
   * `off` asks the route to skip hidden reasoning for this call, for short
   * structured side requests where it only adds latency. Routes that cannot
   * verifiably turn reasoning off ignore it.
   */
  reasoning?: 'off'
  /** Capacity of the final model route, for durable request diagnostics. */
  contextWindow?: number
}

export interface LLMMessageStartEvent {
  type: 'message_start'
  id: string
  model?: string
}

export interface LLMMessageDeltaEvent {
  type: 'message_delta'
  id: string
  delta: string
}

/** Visible reasoning ("thinking") text, streamed before or between answer text. */
export interface LLMReasoningDeltaEvent {
  type: 'reasoning_delta'
  id: string
  delta: string
}

export interface LLMToolCallStartEvent {
  type: 'toolcall_start'
  id: string
  index: number
  name: string
}

export interface LLMToolCallEndEvent {
  type: 'toolcall_end'
  id: string
  index: number
  name: string
  arguments: unknown
}

export interface LLMMessageStopEvent {
  type: 'message_stop'
  id: string
  finishReason: AgentFinishReason
  /** Cost the provider reported for this response, when it reported any. */
  usage?: ModelUsage
}

export type LLMStreamEvent =
  | LLMMessageStartEvent
  | LLMMessageDeltaEvent
  | LLMReasoningDeltaEvent
  | LLMToolCallStartEvent
  | LLMToolCallEndEvent
  | LLMMessageStopEvent

export interface LLMStreamRequestEvent {
  index: number
  messages: ModelMessage[]
  tools: readonly ToolDefinition[]
  options: CompleteOptions
}

export interface LLMAdapter {
  complete(
    messages: readonly ModelMessage[],
    tools: readonly ToolDefinition[],
    options: CompleteOptions,
  ): Promise<LLMCompletion>
  stream?(
    messages: readonly ModelMessage[],
    tools: readonly ToolDefinition[],
    options: CompleteOptions,
  ): AsyncIterable<LLMStreamEvent>
}

export interface AgentInput {
  /**
   * Caller's stable id for this submission. A retried submission with the
   * same id is admitted once: the inbox keeps it as the message id.
   */
  requestId?: string
  text?: string
  /** Images sent with `text` as one user message. */
  attachments?: ModelAttachment[]
  messages?: ModelMessage[]
  context?: unknown
}

export interface AgentStep {
  index: number
  input: readonly ModelMessage[]
  completion: LLMCompletion
  toolResults: readonly ToolResult[]
}

export interface AgentRunResult {
  input: AgentInput
  output: string
  finishReason: AgentFinishReason
  /** Durable turn number this run executed, when a turn was opened. */
  turn?: number
  steps: readonly AgentStep[]
  messages: readonly ModelMessage[]
}

/** Awaited after durable turn/end, before the final Stream Event. */
export interface AgentRunCompletedEvent {
  agentId?: string
  session: SessionLog
  result: AgentRunResult
  signal?: AbortSignal
}

export interface AgentRunOptions {
  maxTurns?: number
  maxSteps?: number
  signal?: AbortSignal
  contextBudget?: AgentContextBudget
  /** Reuse a durable turn opened by the live inbox driver. */
  turn?: number
}

/** Pulls inputs admitted at a live agent's next-step boundary. */
export type AgentNextStepClaimer = () => Promise<readonly AgentInput[]>

export interface AgentContextBudget {
  limit?: number
  /** Share of the window at which compaction is considered (default 0.75). */
  compactRatio?: number
  /**
   * Share of the window at which a run compacts even when `nearEnd` says it is
   * about to finish (default 0.9), so one more long tool result cannot
   * overflow the window.
   */
  hardRatio?: number
  keepTokens?: number
  summarize?: AgentContextSummarizer
  /**
   * Asked once per Agent Run when it crosses `compactRatio` between steps:
   * whether the work is about to finish. A run that is nearly done keeps its
   * full context (compacting would cost a summary and lose detail just before
   * the answer) until `hardRatio`; the next run starts with a compaction.
   */
  nearEnd?: AgentContextNearEndJudge
}

export type AgentContextNearEndJudge = (
  messages: readonly ModelMessage[],
  usage: ContextUsage,
) => boolean | Promise<boolean>

export type AgentContextSummarizer = (
  messages: readonly ModelMessage[],
  usage: ContextUsage,
) => readonly ModelMessage[] | Promise<readonly ModelMessage[]>

export interface AgentContextCompactEvent {
  type: 'agent/context-compact'
  messagesBefore: number
  tokensBefore: number
  limit: number
  keepTokens: number
  messagesAfter: number
}

/** Awaited after a durable compaction; consumers may curate long-lived memory. */
export interface AgentContextCompactedEvent {
  /** The live Agent whose context was compacted, when it has an identity. */
  agentId?: string
  summary: string
  sourceMessages: readonly ModelMessage[]
  llm: LLMAdapter
}

export interface AgentHooks {
  beforeRun?: (input: AgentInput, options: AgentRunOptions) => void | Promise<void>
  afterRun?: (result: AgentRunResult, options: AgentRunOptions) => void | Promise<void>
}

export interface AgentStartEvent {
  input: AgentInput
  options: AgentRunOptions
  injected: ReadonlyMap<string, unknown>
}

export interface AgentTurnStartEvent {
  agentId?: string
  input: AgentInput
  messages: readonly ModelMessage[]
  injected: ReadonlyMap<string, unknown>
}

export interface AgentStepEvent {
  agentId?: string
  index: number
  turn?: number
  step?: number
  input: readonly ModelMessage[]
}

export interface AgentPreStepEvent {
  agentId?: string
  index: number
  /** Durable turn that will own the proposed step. */
  turn?: number
  /** Durable step proposed by the loop. */
  step?: number
  /** Cancellation signal for the current turn. */
  signal?: AbortSignal
  messages: ModelMessage[]
  /** The first system message belongs to the request header, even on an empty Session. */
  requestHeaderOwnsSystem?: boolean
  /** Newly claimed user messages for this proposed step, excluding history. */
  claimedMessages: ModelMessage[]
  /** Admit this proposed step, or end the turn before a model request. */
  admission?: 'enter' | 'reject'
  /** Begin a distinct model-message series before this step's admitted messages. */
  startsRequestSeries?: boolean
}

export interface AgentRequestEvent {
  /** The live Agent making the request, when it has an identity. */
  agentId?: string
  index: number
  /** Read-only snapshot of the model-visible messages for this request. */
  readonly messages: readonly ModelMessage[]
  tools: readonly ToolDefinition[]
  options: CompleteOptions
}

export interface AgentRequestErrorEvent {
  index: number
  turn: number
  step: number
  messages: readonly ModelMessage[]
  tools: readonly ToolDefinition[]
  options: CompleteOptions
  attempt: number
  error: unknown
  /** Normalized durable failure fact surfaced to recovery listeners. */
  failure: { name: string; message: string; stack?: string }
  provider?: string
  model?: string
  signal?: AbortSignal
  retryPolicy?: { maxRetries: number; retryDelayMs: number }
}

export type AgentRequestRetryDecision =
  | { kind: 'retry'; delayMs?: number }
  | undefined

/**
 * Asked once per Agent Run before the loop starts, with the limits the caller
 * set (`undefined` when it set none). A plugin may fill in defaults, such as a
 * high backstop when it watches for stalls itself; whatever stays unset falls
 * back to 64.
 */
export interface AgentStepLimitsEvent {
  agentId?: string
  maxTurns?: number
  maxSteps?: number
}

/**
 * Awaited after each step is durably closed and its tool results recorded,
 * before the loop claims next-step input, so a listener that steers the Agent
 * here reaches the very next step.
 */
export interface AgentStepEndEvent {
  agentId?: string
  index: number
  turn: number
  completion: LLMCompletion
  calls: readonly { call: LLMToolCall; result: ToolResult }[]
  signal?: AbortSignal
}

export interface AgentTurnStoppingEvent {
  /** The live Agent whose turn is stopping, when it has an identity. */
  agentId?: string
  index: number
  turn?: number
  steps: readonly AgentStep[]
  finishReason: AgentFinishReason
}

export interface AgentToolCallEvent {
  agentId?: string
  index: number
  turn?: number
  step?: number
  call: LLMToolCall
}

export interface AgentToolResultEvent {
  agentId?: string
  index: number
  turn?: number
  step?: number
  call: LLMToolCall
  result: ToolResult
}

export interface AgentTurnEndEvent {
  input: AgentInput
  steps: readonly AgentStep[]
  messages: readonly ModelMessage[]
  output: string
  finishReason: AgentFinishReason
}

export type AgentEndEvent = AgentTurnEndEvent

export interface AgentToolStartEvent {
  type: 'tool/start'
  index: number
  turn?: number
  step?: number
  call: LLMToolCall
}

export interface AgentToolEndEvent {
  type: 'tool/end'
  index: number
  turn?: number
  step?: number
  call: LLMToolCall
  result: ToolResult
}

export interface AgentRunEndEvent {
  type: 'run/end'
  run: AgentRunResult
}

/** Transient identity and ordering, scoped to the Agent service lifecycle. */
export type AssistantStreamFrame = { attemptId: string; revision: number } & (
  | { type: 'start'; turn: number; step: number }
  | { type: 'chunk'; index: number; time: number; chunk: AssistantStreamChunk }
  | { type: 'end'; index: number; outcome: {
    kind: 'committed'; eventType: 'assistant/message' | 'assistant/attempt'; seq: number
  } | { kind: 'abandoned' } }
)

export interface AgentAssistantStreamEvent {
  type: 'assistant/stream'
  frame: AssistantStreamFrame
}

export type AgentStreamEvent =
  | LLMStreamEvent
  | AgentAssistantStreamEvent
  | AgentToolStartEvent
  | AgentToolEndEvent
  | AgentRunEndEvent

export type AgentLoop = (
  input?: AgentInput,
  options?: AgentRunOptions,
) => Promise<AgentRunResult>
